import { Field, Form, Formik } from "formik"
import { FormGroup } from "reactstrap"

import { SupporterRole } from "@api/schema"
import FilterItem from "@components/common/FilterItem"
import FormikInput from "@components/common/form/FormikInput"
import OrderByComponent from "@components/common/form/OrderByComponent"
import Icon from "@components/common/Icon"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import { SUPPORTER_ROLE_ORDER_AS_FILTER } from "@customer-config"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"
import { IPartnerMarketSearchParams, IPartnerMarketSortType } from "@services/partnermarketHelper"
import { StringBasedStringArray } from "@services/util"

const usedNamespaces: NamespaceShortcut[] = ["partner-market", "supportRequest"]

export const ID_PARTNERMARKET_SEARCH_FORM = "partnermarket_search_form"

interface IProps {
  searchParams: IPartnerMarketSearchParams
  onSubmit: (values: IPartnerMarketSearchParams) => void
}

/**
 * this component provides a form for filtering support requests in the partnermarket
 */
const PartnerMarketForm = (props: IProps): JSX.Element => {
  const { searchParams, onSubmit } = props
  const t = useDynamicTranslation()

  // if the user clicks on a specific SupporterRole
  const searchedSupporterRoles = (
    selectedSupporterRole: SupporterRole
  ): SupporterRole[] => {
    const activeSupporterRoles: SupporterRole[]
      = searchParams?.supporterRoles?.slice()

    // remove clicked item, if it was already chosen
    if (activeSupporterRoles.includes(selectedSupporterRole)) {
      activeSupporterRoles.splice(
        activeSupporterRoles.indexOf(selectedSupporterRole),
        1
      )
    } else {
      // add clicked item, if it was not chosen yet
      activeSupporterRoles.push(selectedSupporterRole)
    }
    return activeSupporterRoles
  }

  const sortOptionsWithTranslationKey: StringBasedStringArray = {}
  Object.values(IPartnerMarketSortType).forEach(key => {
    sortOptionsWithTranslationKey[key] = t("partner-market", "partnerMarketForm.sortingType." + key)
  })

  return <Formik<IPartnerMarketSearchParams>
    initialValues={searchParams}
    onSubmit={onSubmit}
    validateOnChange={false}
    // We need this prop to make formik re-render the form when initialValues change
    // see https://github.com/jaredpalmer/formik/issues/811 and https://formik.org/docs/api/formik#enablereinitialize-boolean
    enableReinitialize
  >
    {({ handleSubmit, setFieldValue, values }) => {
      return (
        <Form
          onSubmit={handleSubmit}
          id={ID_PARTNERMARKET_SEARCH_FORM}
        >
          <div className="filter-item text-center">
            <FilterItem
              isActive={values?.supporterRoles?.length === 0}
              // Reset the filter to look for all SupporterRoles
              onClick={() => {
                setFieldValue("supporterRoles", [])
                onSubmit({ ...values, supporterRoles: [] })
              }}
              buttonRole="submit"
            >
              {t("partner-market", "partnerMarketForm.supporterRoleAll")}
            </FilterItem>
            {
              // not using Object.values(SupporterRole) to assure a order of the filters
              // that match the customer wishes
              // a test assures, that all SupporterRoles are included in the ORDER-array
              SUPPORTER_ROLE_ORDER_AS_FILTER.map((supporterRole: SupporterRole) => <FilterItem
                key={supporterRole}
                isActive={values?.supporterRoles?.includes(supporterRole)}
                onClick={() => {
                  const activeSupporterRoles = searchedSupporterRoles(supporterRole)
                  setFieldValue("supporterRoles", activeSupporterRoles)
                  onSubmit({ ...values, supporterRoles: activeSupporterRoles })
                }}
                buttonRole="submit"
              >
                {t("supportRequest", "supporterRole." + supporterRole)}
              </FilterItem>
              )}
          </div>
          <FormGroup className="partner-market-search-container">
            <FormGroup className="marketplace-search-container">
              <span className="icon" onClick={() => onSubmit(values)}>
                <Icon
                  name="search"
                  size={25}
                  title={t("partner-market", "partnerMarketForm.search")}
                />
              </span>
              <Field
                component={FormikInput}
                help={t("partner-market", "partnerMarketForm.pattern.help")}
                label={t("partner-market", "partnerMarketForm.pattern.label")}
                placeholder={t("partner-market", "partnerMarketForm.pattern.placeholder.requests")}
                maxLength={50}
                name="pattern"
                noGroup={true}
                type="text"
                value={values.pattern}
                className="marketplace-search"
              />
            </FormGroup>
            <OrderByComponent
              sortBy={values.sortBy}
              sortOrder={values.sortOrder}
              sortingOptions={sortOptionsWithTranslationKey}
              onChange={(newSortOrder, newSortBy) => {
                setFieldValue("sortOrder", newSortOrder)
                setFieldValue("sortBy", newSortBy)
                onSubmit({
                  ...values,
                  sortBy: newSortBy as IPartnerMarketSortType,
                  sortOrder: newSortOrder
                })
              }}
            />
          </FormGroup>
        </Form>
      )
    }}
  </Formik>
}

export default withDynamicNamespaces<IProps>(PartnerMarketForm, usedNamespaces)